import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { useSiteContent } from "../context/SiteContentContext";
import { formatApiErrorDetail } from "../lib/api";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const DEFAULT_AUTH_IMAGE = "https://images.pexels.com/photos/38526712/pexels-photo-38526712.jpeg?auto=compress&cs=tinysrgb&w=1400";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const site = useSiteContent();
  const authImage = site.media("auth_hero", DEFAULT_AUTH_IMAGE);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/account";

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      toast.success(mode === "login" ? "Welcome back" : "Welcome to Amraiya");
      navigate(redirect);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="auth-page">
      {/* editable hero image */}
      <div className="relative hidden lg:block overflow-hidden bg-[#efece6]" data-testid="auth-hero">
        <img
          src={authImage}
          alt="Amraiya atelier"
          className="absolute inset-0 h-full w-full object-cover"
          data-testid="auth-hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/5 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-ivory">
          <p className="overline mb-3">Amraiya · Couture &amp; Heritage</p>
          <p className="font-serif text-4xl leading-tight">Heirlooms made<br />for the modern muse.</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-24">
        <div className="w-full max-w-sm">
          <Link to="/" data-testid="auth-logo">
            <img src="/ambrai-logo.png" alt="Amraiya" className="h-28 md:h-32 w-auto" />
          </Link>
          <AnimatePresence mode="wait">
            <motion.div key={mode} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }} className="mt-10">
              <h1 className="font-serif text-4xl tracking-tight mb-2">{mode === "login" ? "Welcome back" : "Create account"}</h1>
              <p className="text-sm text-ink-soft mb-8">{mode === "login" ? "Sign in to your atelier account." : "Join the house of Amraiya."}</p>

              <form onSubmit={submit} className="space-y-5">
                {mode === "register" && (
                  <label className="block">
                    <span className="overline text-ink-soft mb-1.5 block">Full Name</span>
                    <Input value={form.name} onChange={set("name")} required data-testid="auth-name" />
                  </label>
                )}
                <label className="block">
                  <span className="overline text-ink-soft mb-1.5 block">{mode === "login" ? "Email or username" : "Email"}</span>
                  <Input type={mode === "login" ? "text" : "email"} value={form.email} onChange={set("email")} required data-testid="auth-email" />
                </label>
                <label className="block">
                  <span className="overline text-ink-soft mb-1.5 block">Password</span>
                  <Input type="password" value={form.password} onChange={set("password")} required minLength={6} data-testid="auth-password" />
                </label>

                {error && <p className="text-sm text-red-700" data-testid="auth-error">{error}</p>}

                <button type="submit" disabled={loading} className="btn-ink w-full py-4 overline disabled:opacity-50" data-testid="auth-submit">
                  {loading ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
                </button>
              </form>

              <p className="mt-6 text-sm text-ink-soft">
                {mode === "login" ? "New to Amraiya? " : "Already have an account? "}
                <button onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} className="text-ink link-underline" data-testid="auth-toggle">
                  {mode === "login" ? "Create an account" : "Sign in"}
                </button>
              </p>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
